import { useEffect, useState } from "react";
import { useParams, useOutletContext } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { getEntryDetail, type MonitoringEntry } from "@/src/services/monitoring_service";
import { MonitoringEntryMatches } from "@/app/(app)/monitoring/[entryId]/_components/monitoring-entry-matches";
import { Button } from "@/components/button";
import { Icon } from "@/components/icons";
import NotFoundPage from "../_errors/NotFoundPage";

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "notfound" }
  | { status: "ready"; entry: MonitoringEntry };

export default function MonitoringEntryPage() {
  const { entryId } = useParams();
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoResolved = ctx?.infoResolved ?? false;
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!entryId) {
      setState({ status: "notfound" });
      return;
    }
    if (!infoResolved) return;
    let active = true;
    setState({ status: "loading" });
    getEntryDetail(entryId).then((res) => {
      if (!active) return;
      if (!res.ok) {
        setState({ status: res.error.code === "not_found" ? "notfound" : "error" });
        return;
      }
      setState({ status: "ready", entry: res.value.entry });
    });
    return () => {
      active = false;
    };
  }, [entryId, infoResolved, reloadKey]);

  if (state.status === "notfound") return <NotFoundPage />;
  if (state.status === "loading") return <DetailLoading />;
  if (state.status === "error") return <DetailError onRetry={() => setReloadKey((k) => k + 1)} />;

  return <MonitoringEntryMatches entry={state.entry} />;
}

function DetailLoading() {
  return (
    <div
      className="flex min-h-screen flex-1 flex-col gap-3 bg-white px-4 py-3 sm:px-8"
      aria-busy="true"
    >
      <div className="h-5 w-48 rounded-[4px] bg-grey-200" />
      <div className="h-8 w-64 rounded-[4px] bg-grey-200" />
      <div className="h-9 w-full rounded-[4px] bg-grey-100" />
      <div className="h-[200px] w-full rounded-[4px] bg-grey-100" />
      <span className="sr-only">Loading entry…</span>
    </div>
  );
}

function DetailError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen flex-1 flex-col items-center justify-center gap-4 bg-white px-4 sm:px-8">
      <div className="flex flex-col items-center gap-1.5 text-center">
        <Icon name="error" size={40} weight={300} className="text-grey-500" />
        <h2 className="text-h4 font-semibold text-black">Couldn&rsquo;t load this entry</h2>
        <p className="w-[260px] text-small text-grey-900">
          Something went wrong while loading the entry. Please try again.
        </p>
      </div>
      <Button
        hierarchy="secondary"
        size="sm"
        leftIcon={<Icon name="refresh" size={20} weight={300} />}
        onClick={onRetry}
      >
        Try again
      </Button>
    </div>
  );
}
